import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ShopPromoCarousel from './shopItems/ShopPromoCarousel';
import ProductProp from './propTypes/productProp';
import MetaContext from '../../contextProviders/MetaContext';
import ShopSearchContent from './shopItems/ShopSearchContent';
import ShopHighlightCarousel from './shopItems/ShopHighlightCarousel';
import Header from '../../commonComponents/pageLayout/header/Header';
import UserProp from '../../commonComponents/propTypes/userProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import CurrencyProp from '../profile/propTypes/currencyProp';
import BrandingProp from '../../commonComponents/propTypes/brandingProp';
import AcademyConfigProp from '../landing/propTypes/academyConfig';
import TermsProp from '../../commonComponents/propTypes/termsProp';
import ShopMainFooter from './ShopMainFooter';
import ShopMainHero from './ShopMainHero';

function ShopMain(props) {
  const {
    t,
    highlightedProducts,
    newProducts,
    promotionProducts,
    theme,
    branding,
    terms,
    languages,
    countries,
    currencies,
    academyConfig,
    profile,
  } = props;

  const { userPortalMenus } = useContext(MetaContext);
  const title =
    userPortalMenus.find(u => u.type === 'marketplace')?.name ||
    t('shop.market_place');

  const fakeQuery = {
    category: '',
    maxPrice: 10000, // it's the default value, maybe I need to change it
    attributes: {},
    sort: '-updatedAt',
    search: '',
    productType: undefined,
  };

  const logo = branding ? branding.horizontalLogo : undefined;
  const mainContainerClass = cx(
    'm-grid__item',
    'm-grid__item--fluid',
    'm-grid m-grid--hor-desktop',
    'm-grid--desktop m-body',
  );

  return (
    <Title title={title}>
      <>
        <Header
          theme={theme}
          logo={logo}
          languages={languages}
          currencies={currencies}
          profile={profile}
          countries={countries}
          userPortalMenus={userPortalMenus}
        />
        <div className={mainContainerClass}>
          <div className="shop shopMain">
            <ShopMainHero t={t} academyConfig={academyConfig} />
            <div className="container custom-container">
              <div className="row">
                {newProducts?.length > 0 && (
                  <>
                    <div className="col-lg-5">
                      <div className="shop-new-products-header">
                        <span className="shop-big-title">
                          {t('shop.new_products')}
                        </span>
                        <span className="shop-description">
                          {t('shop.new_products_description')}
                        </span>
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="m-portlet m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-new-products-carousel">
                        <div className="m--padding-top-5 m--padding-bottom-20 shop-carousel">
                          <ShopPromoCarousel productList={newProducts} />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="container custom-container px-0">
              <div className="row">
                {highlightedProducts?.length > 0 && (
                  <div className="col-lg-12 px-0">
                    <div className="row justify-content-center">
                      <span className="shop-big-title">
                        {t('shop.highlighted')}
                      </span>
                    </div>
                    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-highlighted-products-carousel">
                      <div className="m-portlet__body p-0 shop-carousel px-0">
                        <ShopHighlightCarousel
                          productList={highlightedProducts}
                        />
                      </div>
                    </div>
                    <div className="row justify-content-center">
                      <div className="see-all-products-button-container">
                        <a href="/shop" className="see-all-products-button">
                          {t('shop.see_all_products')}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {promotionProducts.length ? (
              <div className="row my-5 mx-0">
                <div className="col-lg-12 col-md-12 promo-products-carousel px-0">
                  <ShopPromoCarousel
                    carouselId="promo-products-carousel"
                    productList={promotionProducts}
                    type="promotion"
                    isLandingPagePromotion
                  />
                </div>
              </div>
            ) : null}
            <div className="container custom-container">
              <div className="row mt-5">
                <div className="col-lg-12 col-md-12">
                  <div className="row justify-content-center">
                    <span className="shop-big-title">
                      {t('shop.our_products')}
                    </span>
                  </div>
                </div>
                <div className="col-lg-12 col-md-12 landing-page-shop">
                  <ShopSearchContent t={t} query={fakeQuery} isLandingPage />
                </div>
              </div>
            </div>
            <div className="container custom-container mb-5">
              <div className="row justify-content-center">
                <div className="see-all-products-button-container">
                  <a href="/shop" className="see-all-products-button">
                    {t('shop.see_all_products')}
                  </a>
                </div>
              </div>
            </div>
            <ShopMainFooter
              academyConfig={academyConfig}
              terms={terms}
              branding={branding}
            />
          </div>
        </div>
      </>
    </Title>
  );
}

ShopMain.propTypes = {
  t: PropTypes.func.isRequired,
  highlightedProducts: PropTypes.arrayOf(PropTypes.shape(ProductProp.propType))
    .isRequired,
  newProducts: PropTypes.arrayOf(PropTypes.shape(ProductProp.propType))
    .isRequired,
  promotionProducts: PropTypes.arrayOf(PropTypes.shape(ProductProp.propType))
    .isRequired,
  branding: PropTypes.shape(BrandingProp.propType),
  terms: PropTypes.shape(TermsProp.propType),
  theme: PropTypes.string.isRequired,
  profile: PropTypes.shape(UserProp.propType),
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType)),
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  academyConfig: PropTypes.shape(AcademyConfigProp.propType).isRequired,
};

ShopMain.defaultProps = {
  profile: null,
  languages: [],
  currencies: [],
  countries: [],
  branding: null,
  terms: null,
};

export default translate(ShopMain);
